import React from 'react'
import { Col, Row, Button, Card, CardBody } from 'reactstrap'
import { withFirebase } from '../../components/Fb'
import { Calendar, momentLocalizer, Views } from 'react-big-calendar'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import moment from 'moment'
import 'moment/locale/fi'
import Select from 'react-select'
import { Chip, IconButton, Stack, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import FalconModal from '../../components/Inputs/FalconModal'
import TeamSeasonSlotDeleteDialog from '../../components/Dialogs/TeamSeasonSlotDeleteDialog'
import {
  getTeamSeasonSchema,
  getTeamSeasonUiSchema,
  getTeamSeasonSlotSchema,
  getTeamSeasonSlotUiSchema,
} from '../../components/Inputs/dataschemas'

// Joukkuekausien kalenteri. docs/features/teams.md §4.1 and Phase A.
//
// Structure is lifted from RecurringSalePricingCalendarContainer: the calendar's columns are
// the seven weekdays rather than dates, all events live on 1.1.1970 so only the time of day
// matters, and dragging out a range opens the slot editor prefilled with that weekday and time.
//
// Two things differ from every other pricing calendar here, both from §4.1:
//   - a slot may narrow the season's court list, so the event label has to show which courts a
//     slot covers - the weekday grid cannot express it
//   - slots can be deleted, which the pricing calendars cannot do at all
const localizer = momentLocalizer(moment)

const weekdaysAsResources = [
  { resourceId: 1, resourceTitle: 'Maanantai' },
  { resourceId: 2, resourceTitle: 'Tiistai' },
  { resourceId: 3, resourceTitle: 'Keskiviikko' },
  { resourceId: 4, resourceTitle: 'Torstai' },
  { resourceId: 5, resourceTitle: 'Perjantai' },
  { resourceId: 6, resourceTitle: 'Lauantai' },
  { resourceId: 7, resourceTitle: 'Sunnuntai' },
]

const EmptyToolbar = () => <></>

const groupStyles = { display: 'flex', alignItems: 'center', justifyContent: 'space-between' }
const groupBadgeStyles = {
  backgroundColor: '#EBECF0',
  borderRadius: '2em',
  color: '#172B4D',
  display: 'inline-block',
  fontSize: 12,
  fontWeight: 'normal',
  lineHeight: '1',
  minWidth: 1,
  padding: '0.16666666666667em 0.5em',
  textAlign: 'center',
}

const formatGroupLabel = (data) => (
  <div style={groupStyles}>
    <span>{data.label}</span>
    <span style={groupBadgeStyles}>{data.options.length}</span>
  </div>
)

const formatDate = (d) => (d ? moment(d).format('DD.MM.YYYY') : '-')

const PAYER_LABELS = {
  team: 'Joukkue',
  manager: 'Joukkueenjohtaja',
  organisation: 'Kiinteä yritys / seura',
}

// A slot with no resources of its own inherits every court on the season (§4.1). The label has
// to say which of the two it is, because on the weekday grid they look identical otherwise.
const slotCourtLabel = (slot, resourcesById) => {
  if (!slot.resources || slot.resources.length === 0) return 'kaikki kentät'
  return slot.resources
    .map((id) => (resourcesById[id] && resourcesById[id].displayName) || id)
    .sort((a, b) => a.localeCompare(b))
    .join(', ')
}

// react-big-calendar fires onSelectEvent on the whole event wrapper, so the delete control
// has to stop propagation - otherwise removing a slot also opens its editor underneath.
function slotEventFactory(onDelete) {
  return function SlotEvent({ event }) {
    return (
      <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <span>
          <div>{event.title}</div>
          <div style={{ fontSize: '0.75em', opacity: 0.85 }}>{event.courts}</div>
        </span>
        {event.data && event.data.id ? (
          <IconButton
            size='small'
            aria-label='Poista vuoro'
            sx={{ color: 'inherit', p: 0.25 }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              onDelete(event.data)
            }}
          >
            <CloseIcon fontSize='inherit' />
          </IconButton>
        ) : null}
      </span>
    )
  }
}

class TeamSeasonCalendarContainer extends React.Component {
  constructor(props) {
    super(props)
    this.myRef = React.createRef()
    this.state = {
      loading: true,
      seasons: [],
      resources: [],
      formats: [],
      organisations: [],
      seasonOptions: [],
      slots: [],
      selectedSeasonId: null,
      selectedSeason: null,
      editingSeason: null,
      selectedSlot: null,
      seasonModalOpen: false,
      slotModalOpen: false,
      confirmDeleteSlot: null,
      deletingSlot: false,
    }
    this.slotEvent = slotEventFactory(this.askDeleteSlot)
  }

  componentDidMount() {
    this.updateSlotData()
  }

  updateSlotData = async () => {
    try {
      const [resourceSnap, seasonSnap, formatSnap, organisationSnap] = await Promise.all([
        this.props.firebase.getResourses(),
        this.props.firebase.getTeamSeasons(),
        this.props.firebase.getTeamFormats(),
        this.props.firebase.listenOrganisations().get(),
      ])
      // For the fixed-payer picker (organisationWidget), which searches this list locally.
      const organisations = organisationSnap.docs
        .map((d) => ({ ...d.data(), id: d.id }))
        .filter((o) => o.state !== 'deleted')
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'fi'))
      const resources = resourceSnap.docs
        .map((d) => ({ ...d.data(), id: d.id }))
        .filter((r) => r.state !== 'deleted')
      const seasons = seasonSnap.empty
        ? []
        : seasonSnap.docs.map((d) => ({ ...d.data(), id: d.id })).filter((s) => s.state !== 'deleted')
      // Deleted formats are dropped from the picker, but a season that already references one
      // keeps the id: removing it here would silently rewrite the season's own config.
      const formats = formatSnap.empty
        ? []
        : formatSnap.docs.map((d) => ({ ...d.data(), id: d.id })).filter((f) => f.state !== 'deleted')
      this.setState({ resources, seasons, formats, organisations }, this.filterData)
    } catch (error) {
      console.error('Loading team seasons failed', error)
      this.setState({ loading: false, loaderror: error.message })
    }
  }

  filterData = () => {
    const refreshed = this.state.selectedSeasonId
      ? this.state.seasons.find((s) => s.id === this.state.selectedSeasonId) || null
      : null
    const seasonOptions = this.state.seasons
      .map((s) => ({ value: s.id, label: s.name || '(nimetön)', active: s.active }))
      .reduce(
        (acc, current) => {
          if (current.active) acc[0].options.push(current)
          else acc[1].options.push(current)
          return acc
        },
        [
          { label: 'Active', options: [] },
          { label: 'Inactive', options: [] },
        ]
      )
    this.setState({ seasonOptions, loading: false, selectedSeason: refreshed })
    if (this.state.selectedSeasonId) {
      this.updateViewableSeason(this.state.selectedSeasonId)
    }
  }

  resourcesById = () => {
    return this.state.resources.reduce((acc, r) => {
      acc[r.id] = r
      return acc
    }, {})
  }

  // Court and format pickers are populated at runtime, the same way
  // RecurringSalePricingCalendarContainer fills its resources oneOf.
  getSeasonSchema = () => {
    const schema = getTeamSeasonSchema()
    schema.properties.resources.items = {
      type: 'string',
      oneOf: [...this.state.resources]
        .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''))
        .map((r) => ({ const: r.id, title: r.displayName })),
    }
    schema.properties.formats.items = {
      type: 'string',
      oneOf: [...this.state.formats]
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        .map((f) => ({ const: f.id, title: f.name })),
    }
    return schema
  }

  // The fixed-payer organisation list goes in through ui:options, which is where rjsf hands a
  // widget its per-field config. Copied, not assigned: getTeamSeasonUiSchema() hands back the
  // shared module object.
  getSeasonUiSchema = () => {
    const uiSchema = getTeamSeasonUiSchema()
    return {
      ...uiSchema,
      payerorganisationid: {
        ...uiSchema.payerorganisationid,
        'ui:options': { organisations: this.state.organisations },
      },
    }
  }

  // What the details card says after "Maksaja". For a fixed organisation the mode alone does not
  // tell the venue WHICH one, so the name is resolved - and an id that no longer resolves is said
  // out loud rather than shown as a bare mode that looks configured.
  payerText = (season) => {
    const mode = PAYER_LABELS[season.payerdefault] || '-'
    if (season.payerdefault !== 'organisation') return mode
    if (!season.payerorganisationid) return mode + ' (yritystä ei valittu)'
    const org = this.state.organisations.find((o) => o.id === season.payerorganisationid)
    return mode + ': ' + (org ? org.name || org.id : '(yritystä ei löydy)')
  }

  // The slot's court list is narrowed against THE SEASON's courts, not against every court in
  // the venue: a slot may never widen what the season allows (§4.1).
  getSlotSchema = () => {
    const schema = getTeamSeasonSlotSchema()
    const season = this.state.selectedSeason
    const allowed = season && season.resources && season.resources.length ? season.resources : null
    const byId = this.resourcesById()
    const pool = allowed ? allowed.map((id) => byId[id]).filter(Boolean) : this.state.resources
    schema.properties.resources.items = {
      type: 'string',
      oneOf: pool
        .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''))
        .map((r) => ({ const: r.id, title: r.displayName })),
    }
    return schema
  }

  selectSeason = (event) => {
    this.updateViewableSeason(event.value)
  }

  updateViewableSeason = (id) => {
    const season = this.state.seasons.find((s) => s.id === id)
    if (!season) return
    const byId = this.resourcesById()
    let slots = []
    if (season.slots) {
      slots = Object.keys(season.slots)
        .map((key) => {
          const slot = season.slots[key]
          if (!slot || !slot.days) return []
          const start = moment(slot.starttime, 'HH:mm').year(1970).month(0).date(1).toDate()
          const end = moment(slot.endtime, 'HH:mm').year(1970).month(0).date(1).toDate()
          const title = slot.displayName || slot.starttime + ' - ' + slot.endtime
          const courts = slotCourtLabel(slot, byId)
          return slot.days.map((d) => ({
            resourceId: d,
            data: { ...slot },
            title,
            courts,
            start,
            end,
          }))
        })
        .flat()
    }
    this.setState({ selectedSeasonId: id, selectedSeason: season, slots })
  }

  // --- season modal ---------------------------------------------------------

  toggleSeasonModal = (mode) => {
    // 'new' edits nothing; anything else edits the season currently on the calendar. Note this
    // must not touch selectedSeason - the slot editor narrows its court list against it (§4.1),
    // and clearing it here would silently widen that list back to every court in the venue.
    this.setState({
      editingSeason: mode === 'new' ? null : this.state.selectedSeason,
      seasonModalOpen: !this.state.seasonModalOpen,
    })
  }

  onSeasonSaved = async () => {
    await this.updateSlotData()
  }

  createSeasonModal = () => {
    return (
      <FalconModal
        onCreate={this.props.firebase.createTeamSeason}
        onUpdate={this.props.firebase.updateTeamSeason}
        modifiedCallback={this.onSeasonSaved}
        toggle={this.toggleSeasonModal}
        cancel={this.toggleSeasonModal}
        resource={this.state.editingSeason}
        isOpen={this.state.seasonModalOpen}
        uiSchema={this.getSeasonUiSchema()}
        dataSchema={this.getSeasonSchema()}
        title={this.state.editingSeason && this.state.editingSeason.id ? 'Muokkaa kautta' : 'Uusi kausi'}
      />
    )
  }

  // --- slot modal -----------------------------------------------------------

  toggleSlotModal = () => {
    this.setState({ slotModalOpen: !this.state.slotModalOpen })
  }

  // Cancelling a brand new slot has to drop the placeholder event the drag created, or it
  // lingers on the grid as a slot that was never saved.
  toggleSlotModalCancel = () => {
    this.setState({ slots: this.state.slots.filter((e) => e.id !== 'placeholder') }, this.toggleSlotModal)
  }

  handleSelectEvent = (event) => {
    this.setState({ selectedSlot: JSON.parse(JSON.stringify(event.data)) }, this.toggleSlotModal)
  }

  handleSelectSlot = (slotInfo) => {
    if (!this.state.selectedSeasonId) return
    const s = moment(slotInfo.start)
    let e = moment(slotInfo.end)
    if (e.diff(s, 'minutes') < 30) e = moment(s).add(30, 'minutes')
    const placeholder = {
      id: 'placeholder',
      start: slotInfo.start,
      end: e.toDate(),
      resourceId: slotInfo.resourceId,
      title: '',
      courts: '',
    }
    this.setState({
      slots: [...this.state.slots, placeholder],
      selectedSlot: {
        configid: this.state.selectedSeasonId,
        days: [slotInfo.resourceId],
        pricingunit: 'h',
        resources: [],
        starttime: s.format('HH:mm'),
        endtime: e.format('HH:mm'),
      },
    })
    this.toggleSlotModal()
  }

  onSlotSaved = async () => {
    await this.updateSlotData()
  }

  createSlotModal = () => {
    return (
      <FalconModal
        onCreate={this.props.firebase.addTeamSeasonSlot}
        onUpdate={this.props.firebase.addTeamSeasonSlot}
        modifiedCallback={this.onSlotSaved}
        toggle={this.toggleSlotModal}
        cancel={this.toggleSlotModalCancel}
        resource={this.state.selectedSlot}
        isOpen={this.state.slotModalOpen}
        uiSchema={getTeamSeasonSlotUiSchema()}
        dataSchema={this.getSlotSchema()}
        title={this.state.selectedSlot && this.state.selectedSlot.id ? 'Muokkaa vuoroa' : 'Uusi vuoro'}
      />
    )
  }

  // --- slot deletion --------------------------------------------------------

  askDeleteSlot = (slot) => {
    if (slot && slot.id) this.setState({ confirmDeleteSlot: slot })
  }

  cancelDeleteSlot = () => {
    this.setState({ confirmDeleteSlot: null })
  }

  confirmDeleteSlot = async () => {
    const slot = this.state.confirmDeleteSlot
    if (!slot || !slot.id) return
    this.setState({ deletingSlot: true })
    try {
      await this.props.firebase.deleteTeamSeasonSlot(this.state.selectedSeasonId, slot.id)
      await this.updateSlotData()
      this.setState({ confirmDeleteSlot: null, deletingSlot: false })
    } catch (error) {
      console.error('Deleting slot failed', error)
      this.setState({ deletingSlot: false })
    }
  }

  // --- render ---------------------------------------------------------------

  renderSeasonDetails = () => {
    const season = this.state.seasons.find((s) => s.id === this.state.selectedSeasonId)
    if (!season) return <></>
    const byId = this.resourcesById()
    const courts = (season.resources || [])
      .map((id) => (byId[id] && byId[id].displayName) || id)
      .sort((a, b) => a.localeCompare(b))
    return (
      <Card>
        <CardBody onClick={() => this.toggleSeasonModal()} style={{ cursor: 'pointer' }}>
          <strong>{season.active ? '' : 'Not '}Active</strong>
          <br />
          <strong>Kausi</strong>
          {': ' + formatDate(season.validstart) + ' - ' + formatDate(season.validend)}
          <br />
          <strong>Varaaminen</strong>
          {': ' + formatDate(season.bookingopens) + ' - ' + formatDate(season.bookingcloses)}
          <br />
          <strong>Varattavissa etukäteen</strong>
          {': ' + (season.bookingdaysahead > 0 ? season.bookingdaysahead + ' pv' : 'ei rajaa')}
          <br />
          <strong>Maksaja</strong>
          {': ' + this.payerText(season)}
          <br />
          <strong>Kentät</strong>
          {': '}
          {courts.length ? (
            <Stack direction='row' spacing={0.5} sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 0.5 }}>
              {courts.map((c) => (
                <Chip key={c} label={c} size='small' />
              ))}
            </Stack>
          ) : (
            'ei valittuja kenttiä'
          )}
        </CardBody>
      </Card>
    )
  }

  render() {
    if (this.state.loading) {
      return (
        <Row>
          <Col>Loading ...</Col>
        </Row>
      )
    }
    return (
      <div className='content' style={{ height: '75vh' }}>
        <Row>
          <Col md='4'>
            <Card>
              <CardBody>
                <Select
                  styles={{ menu: (provided) => ({ ...provided, zIndex: 9999 }) }}
                  closeMenuOnSelect={true}
                  name='teamseasons'
                  options={this.state.seasonOptions}
                  formatGroupLabel={formatGroupLabel}
                  className='basic-multi-select'
                  classNamePrefix='select'
                  placeholder='Kaudet ...'
                  onChange={this.selectSeason}
                />
              </CardBody>
            </Card>
          </Col>
          <Col>
            <Button onClick={() => this.toggleSeasonModal('new')}>Lisää kausi</Button>
          </Col>
        </Row>
        {this.state.loaderror ? (
          <Row>
            <Col>
              <Typography color='error'>Kausien lataus epäonnistui: {this.state.loaderror}</Typography>
            </Col>
          </Row>
        ) : null}
        <Row>
          <Col>{this.renderSeasonDetails()}</Col>
        </Row>
        <Row>
          <Col md='12'>
            <Card>
              <CardBody>
                {!this.state.selectedSeasonId ? (
                  <Typography variant='body2' sx={{ mb: 1, opacity: 0.7 }}>
                    Valitse kausi yltä, tai lisää uusi. Vuorot lisätään maalaamalla kalenteriin.
                  </Typography>
                ) : null}
                <Calendar
                  style={{ height: '85vh' }}
                  ref={this.myRef}
                  selectable={!!this.state.selectedSeasonId}
                  localizer={localizer}
                  events={this.state.slots || []}
                  views={['day']}
                  step={30}
                  min={new Date(1970, 0, 1, 0, 0, 0)}
                  defaultView={Views.DAY}
                  scrollToTime={new Date(1970, 0, 1, 12)}
                  defaultDate={new Date(1970, 0, 1, 12)}
                  onSelectEvent={this.handleSelectEvent}
                  onSelectSlot={this.handleSelectSlot}
                  components={{ toolbar: EmptyToolbar, event: this.slotEvent }}
                  resources={weekdaysAsResources}
                  resourceIdAccessor='resourceId'
                  resourceTitleAccessor='resourceTitle'
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
        {this.createSlotModal()}
        {this.createSeasonModal()}
        <TeamSeasonSlotDeleteDialog
          open={!!this.state.confirmDeleteSlot}
          slot={this.state.confirmDeleteSlot}
          working={this.state.deletingSlot}
          onConfirm={this.confirmDeleteSlot}
          onCancel={this.cancelDeleteSlot}
        />
      </div>
    )
  }
}

export default withFirebase(TeamSeasonCalendarContainer)
